import styles from './DashboardHeader.module.scss';
import { ACCESS_TYPES, DATA_POSITION, MAGIC_UPLOAD_TYPE } from '../../../../store/investor/constants';
import { FEATURE_STATUS } from '../../../../store/user/constants';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { backButtonImg, backButtonImgHovered } from './TabConstants';
import ReportNavigation from './components/ReportNavigation/ReportNavigation';
import HeaderButtonContainer from './components/HeaderButtonContainer/HeaderButtonContainer';
import { useEffect, useState, useRef } from 'react';
import { INVESTOR_ROUTES } from '../../../../utils/constants/routesConst';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import { commentsIcon, EDIT_PROFILE_ICON, magicUploadIcon, magicUploadIconColor } from '../../../../assets/hostedassets';
import { closeCommentsThread, setShowCommentsThread } from '../../../../store/commentsThread/commentsThread_actions';
import clsx from 'clsx';
import { removeTooltipContent, setTooltipContent } from '../../../../store/tooltip/tooltip_actions';
import { USER_TYPE } from '../../../../utils/constants/user';
import { Mixpanel } from '../../../../utils/mixpanel';
import { getSubdomain } from '../../../../utils/utils';
import { AICA_MAGIC_UPLOAD, AICA_VIEW_COMMENT } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { setDealDrawer } from '../../../../store/investor/action';
import { getCompanyLogo, getFaviconUrl } from '../../../../utils/urlMetadata';
import { useVisibilityConfig } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';
import { TOPBAR_CONSTANTS } from '../../../../common/Topbar/Topbar';
import AIText from '../../../../common/AIText/AIText';
import ConfigurableModal from '../../../../common/ConfigurableModal/ConfigurableModal';
import { useLocation } from 'react-router-dom';
import { DEBT_PRODUCTS_EXIT_WITHOUT_SAVING } from '../../../../utils/constants/commonConstants';

type DashboardHeaderProps = {
    isUserRestricted: boolean;
    selectedCompany: any;
    metricScoreGenerated?: boolean;
    refreshReportStatus: string;
    downloadPDFStatus: string;
    goBackToPortfolio: () => void;
    renderHeaderButton: () => void;
    exportData: () => void;
};

const DashboardHeader = (props: DashboardHeaderProps) => {
    const {
        isUserRestricted,
        selectedCompany,
        metricScoreGenerated,
        refreshReportStatus,
        downloadPDFStatus,
        goBackToPortfolio,
        exportData,
        renderHeaderButton,
    } = props;
    const dispatch = useDispatch();
    const { isVisible } = useVisibilityConfig();
    const isDSInvestor = localStorage.getItem('isDSInvestor') === 'true';
    const permissions = useAppSelector((state) => state.investor.permissions);
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const userData = useAppSelector((state) => state.user.userData);
    const untaggedCount = useAppSelector((state) => state.investor.untaggedCount);
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [showExitModal, setShowExitModal] = useState(false);
    const updatedLendersMap = useAppSelector((state) => state.investor.dealApplicationDrawer.updatedLendersMap);
    const logoContainerRef = useRef<HTMLDivElement | null>(null);
    const [selected, setSelected] = useState<any>({
        id: 'report',
        label: 'Aica Report',
        img: 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/AicaReportHeaderIcon.svg',
        route: '',
        isLocked: false,
    });
    const isMagicUploadOpen =
        searchParams.get('showNeedInfo') === 'true' &&
        searchParams.get('isCompany') === 'true' &&
        searchParams.get('magicUpload') === 'true';

    const isPartnerOrLender = [USER_TYPE.LENDER, USER_TYPE.LENDER_TEAM, USER_TYPE.PARTNER].includes(userData.accountType);
    useEffect(() => {
        if (selected.id === 'dataroom') {
            navigate(`${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.DATA_ROOM_DETAILS}${params.id ?? ''}?${searchParams.toString()}`);
        }
    }, [selected]);

    // Function to create placeholder with first letter of company name
    const createPlaceholder = () => {
        if (!logoContainerRef.current) return;

        // Clear any existing content
        logoContainerRef.current.innerHTML = '';

        // Create placeholder element with first letter
        const placeholder = document.createElement('div');
        placeholder.className = styles.FavIcon;
        placeholder.textContent = selectedCompany?.orgName?.slice(0, 1) || '';

        logoContainerRef.current.appendChild(placeholder);
    };

    const openCloseCommentsThread = () => {
        if (commentsThreadState.showCommentsThread) {
            dispatch(closeCommentsThread());
        } else {
            dispatch(
                setShowCommentsThread({
                    source: 'DASHBOARD',
                    assesseeOrgId: params.id as string,
                }),
            );

            Mixpanel.track(AICA_VIEW_COMMENT, {
                userRole: userData?.accountType,
                pnoPlatform: getSubdomain(),
                pnoId: userData?.investorOrgId,
                userId: userData?.userId,
                currentUrl: window.location.href,
                accessibility: DATA_POSITION.REPORT,
                organizationId: params.id,
            });
        }
    };

    const openMagicUpload = () => {
        searchParams.set('showNeedInfo', 'true');
        searchParams.set('isCompany', 'true');
        searchParams.set('magicUpload', 'true');
        setSearchParams(searchParams);

        Mixpanel.track(AICA_MAGIC_UPLOAD, {
            userRole: userData?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: userData?.investorOrgId,
            userId: userData?.userId,
            currentUrl: window.location.href,
            accessedPlatform: userData?.accountType,
            individualGlobal: MAGIC_UPLOAD_TYPE.INDIVIDUAL,
            organizationId: params.id,
        });
    };

    const showTooltipForName = (e) => {
        const target = e.currentTarget;
        if (target.scrollWidth <= target.clientWidth) return;
        dispatch(
            setTooltipContent({
                content: selectedCompany?.orgName,
                target: e.currentTarget,
                placement: 'bottom',
            }),
        );
    };

    const hideTooltip = () => {
        removeTooltipContent(dispatch);
    };

    const handleGoBack = () => {
        const isCurrentlyOnDebtProducts = location.pathname.includes('debt-products');
        const hasUnsavedChanges = updatedLendersMap && updatedLendersMap.length > 0;

        if (isCurrentlyOnDebtProducts && hasUnsavedChanges) {
            setShowExitModal(true);
        } else {
            goBackToPortfolio();
        }
    };

    return (
        <div className={styles.DashboardHeader}>
            <div className={styles.CompanyName}>
                {!isUserRestricted && (
                    <img
                        onMouseEnter={(e) => (e.currentTarget.src = backButtonImgHovered)}
                        onMouseLeave={(e) => (e.currentTarget.src = backButtonImg)}
                        alt="back"
                        src={backButtonImg}
                        className={styles.HeaderBackButton}
                        onClick={handleGoBack}
                    />
                )}
                <div className={styles.Name}>
                    <div ref={logoContainerRef} className={styles.LogoContainer}>
                        {(() => {
                            const companyLogo = selectedCompany?.logo;
                            const websiteUrl = selectedCompany?.websiteUrl;
                            const logoFromWebsite = websiteUrl && getCompanyLogo(websiteUrl);
                            const faviconFromWebsite = websiteUrl && getFaviconUrl(websiteUrl);
                            const logoSrc = companyLogo || logoFromWebsite || faviconFromWebsite;

                            if (logoSrc) {
                                return (
                                    <img
                                        src={logoSrc}
                                        alt="Company Logo"
                                        className={styles.CompanyLogo}
                                        onError={(e) => {
                                            // Hide the failed image
                                            e.currentTarget.style.display = 'none';

                                            // Try favicon as fallback if we're using the logo from website
                                            if (logoSrc === logoFromWebsite && faviconFromWebsite) {
                                                const newImg = document.createElement('img');
                                                newImg.src = faviconFromWebsite;
                                                newImg.className = styles.CompanyLogo;
                                                newImg.alt = 'Company Logo';

                                                // Add error handler for favicon as well
                                                newImg.onerror = () => {
                                                    newImg.style.display = 'none';
                                                    // Create placeholder with first letter
                                                    createPlaceholder();
                                                };

                                                // Replace the original image
                                                if (logoContainerRef.current) {
                                                    logoContainerRef.current.innerHTML = '';
                                                    logoContainerRef.current.appendChild(newImg);
                                                }
                                            } else {
                                                // Create placeholder with first letter
                                                createPlaceholder();
                                            }
                                        }}
                                    />
                                );
                            } else {
                                return <span className={styles.FavIcon}>{selectedCompany?.orgName?.slice(0, 1) ?? ''}</span>;
                            }
                        })()}
                    </div>
                    <div className={styles.NameTextContainer}>
                        <div
                            className={styles.NameText}
                            title={selectedCompany?.orgName}
                            onMouseOver={showTooltipForName}
                            onMouseLeave={hideTooltip}
                        >
                            {selectedCompany?.orgName}
                        </div>
                        <div className={styles.SubText}>Created on {dayjs(selectedCompany?.dateCreated).format('DD MMM YYYY')}</div>
                    </div>
                </div>
                {/* {selectedCompany?.companyAccessType !== ACCESS_TIERS.WATCH &&
                   metricScoreGenerated && (
                       <AicaGradeBatch
                           showAicaGradeInfo={() => dispatch(setShowAicaGradeDrawer(true))}
                       />
                   )} */}
            </div>
            <div className={styles.DashboardHeaderMiddle}>
                {!isDSInvestor && <ReportNavigation selectedCompany={selectedCompany} />}
                {/*<TabSlider*/}
                {/*    selected={selected}*/}
                {/*    onChange={(tab) => {*/}
                {/*        setSelected(tab);*/}
                {/*    }}*/}
                {/*    navbarItems={[...tabOptions]}*/}
                {/*/>*/}
            </div>
            <div className={styles.DashboardHeaderRight}>
                <>
                    {permissions?.TRACK !== ACCESS_TYPES.LOCKED && renderHeaderButton()}
                    {isVisible(VISIBILITY_KEYS.MAGIC_UPLOAD.ENABLED) &&
                        (!isPartnerOrLender ? (
                            <div
                                className={clsx(styles.MessageIcon, {
                                    [styles.MessageIcon_Active]: isMagicUploadOpen,
                                })}
                                onClick={openMagicUpload}
                            >
                                <img src={magicUploadIcon} alt="comments" height={'30px'} />
                            </div>
                        ) : (
                            <div style={{ position: 'relative' }}>
                                <div className={styles.MagicUploadButton} onClick={openMagicUpload}>
                                    <img src={magicUploadIconColor} alt={'MagicUploadIcon'} height={'16px'} width={'16px'} />
                                    <AIText text={TOPBAR_CONSTANTS.BUTTONS.MAGIC_UPLOAD} style={{ fontSize: '0.875rem' }} />
                                </div>
                            </div>
                        ))}
                    {!isPartnerOrLender && (
                        <>
                            <div
                                className={clsx(styles.MessageIcon, {})}
                                onClick={() => {
                                    dispatch(
                                        setDealDrawer({
                                            drawerSource: 'Debt Products',
                                            open: true,
                                            drawerLabel: 'Edit Profile',
                                            companyName: selectedCompany?.orgName,
                                        }),
                                    );
                                    dispatch(closeCommentsThread());
                                }}
                            >
                                <img src={EDIT_PROFILE_ICON} alt="comments" height={'30px'} />
                            </div>
                            <div
                                className={clsx(styles.MessageIcon, {
                                    [styles.MessageIcon_Active]:
                                        commentsThreadState.showCommentsThread && !commentsThreadState.closingCommentsThread,
                                })}
                                onClick={openCloseCommentsThread}
                            >
                                <img src={commentsIcon} alt="comments" height={'30px'} />
                            </div>
                        </>
                    )}

                    <HeaderButtonContainer
                        exportData={exportData}
                        selectedCompany={selectedCompany}
                        refreshReportStatus={refreshReportStatus}
                        downloadPDFStatus={downloadPDFStatus}
                    />
                    {refreshReportStatus && refreshReportStatus === FEATURE_STATUS.SHOW && (
                        <div className={styles.RefreshReport}>Refresh Report</div>
                    )}
                    {downloadPDFStatus && downloadPDFStatus === FEATURE_STATUS.SHOW && (
                        <div className={styles.DownloadPDF}>Download PDF</div>
                    )}
                </>
            </div>
            {showExitModal && (
                <ConfigurableModal
                    isOpen={showExitModal}
                    onClose={() => {
                        setShowExitModal(false);
                    }}
                    heading={DEBT_PRODUCTS_EXIT_WITHOUT_SAVING.HEADING}
                    message={DEBT_PRODUCTS_EXIT_WITHOUT_SAVING.MESSAGE}
                    primaryButton={{
                        label: 'Continue',
                        onClick: () => {
                            goBackToPortfolio();
                            setShowExitModal(false);
                        },
                        style: { backgroundColor: '#6021B3' },
                    }}
                    secondaryButton={{
                        label: 'Cancel',
                        onClick: () => {
                            setShowExitModal(false);
                        },
                    }}
                />
            )}
        </div>
    );
};

export default DashboardHeader;
